import { Injectable } from '@angular/core';
import { IDBPDatabase, openDB } from "idb";
import { Db } from "../models/Db";
import { Image } from "../models/Image";
import { UploadService } from "./upload.service";
import { global } from "./global";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { AuthService } from "./auth.service";
import { lastValueFrom } from "rxjs";

// Lecturas fallidas seguidas de una foto de la cola y desde cuándo falla (ms)
interface ReadFailure {
    count: number;
    since: number;
}

@Injectable({
    providedIn: 'root'
})
export class DbService {

    // La cola se relee cada minuto: 3 fallos en 10 minutos descartan un error puntual del navegador
    private static readonly READ_FAILURES_TO_DELETE = 3;
    private static readonly READ_FAILURE_MIN_AGE_MS = 10 * 60 * 1000;
    private static readonly READ_FAILURES_KEY = 'pendingPhotosReadFailures';
    // Espera antes de reintentar una foto que el servidor rechaza, según los intentos fallidos
    private static readonly RETRY_DELAYS_MINUTES = [1, 5, 15, 60];
    // Inicio en base64 de un JPEG y de un PNG: un POD que no empieza así no es una foto
    private static readonly IMAGE_BASE64_PREFIXES = ['/9j/', 'iVBORw0KGgo'];

    uploadingImages: boolean = false;
    // Fotos en cola: el aviso de fotos pendientes se enseña mientras haya alguna
    pendingCount: number = 0;
    private refreshingCount: boolean = false;
    private refreshCountAgain: boolean = false;
    url: string;
    urlCropper: string;

    private dbPromise: Promise<IDBPDatabase<Db>>;

    constructor(
        private uploadService: UploadService,
        private http: HttpClient,
        private auth: AuthService
    ) {
        this.url = global.getUrl();
        this.urlCropper = global.getUrlCropper();

        this.dbPromise = openDB<Db>('imagesDB', 1, {
            upgrade(db) {
                let activitatsStore = db.createObjectStore('images', {keyPath: 'id', autoIncrement: true});
            }
        });
        this.refreshPendingCount();
    }

    async addImage(item: Image) {
        if (item.tipoFoto === "albaranFoto") {
            if (!item.image) throw new Error('No hay foto que guardar');
            await this.toBytes(item);
        } else if (item.tipoFoto === "finalitzacioAlbara" && !this.isImageBase64(item.base64)) {
            throw new Error('El POD no es una imagen');
        }

        item.uploadId = item.uploadId ?? this.newUploadId();
        const db = await this.dbPromise;
        const key = await db.put('images', item);
        this.refreshPendingCount();
        return key;
    }

    // crypto.randomUUID no existe en iOS anterior a 15.4
    private newUploadId(): string {
        if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

        const bytes = crypto.getRandomValues(new Uint8Array(16));
        return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    }

    /**
     * Cola de fotos que se pueden enviar: solo esas se cuentan, se listan y se suben.
     * Se lee foto a foto y no con getAll: en iOS una foto ilegible hace fallar la lectura de toda la cola.
     * Se devuelven sin los bytes para no tener toda la cola en memoria; se leen al enviar (readFile).
     */
    async existsImages(): Promise<Image[]> {
        const db = await this.dbPromise;
        const keys = await db.getAllKeys('images');
        const failures = this.loadReadFailures();
        const images: Image[] = [];

        for (const key of keys) {
            const state = await this.checkImage(db, key);

            if (state.image) {
                delete failures[key];
                const {bytes, image, ...withoutBytes} = state.image;
                images.push(withoutBytes);
            } else if (state.damaged || this.isReadFailurePermanent(failures, key)) {
                delete failures[key];
                await db.delete('images', key);
            }
        }

        // Las marcas de fotos que ya no están en la cola sobran
        Object.keys(failures).filter(key => !keys.includes(Number(key))).forEach(key => delete failures[Number(key)]);
        this.saveReadFailures(failures);

        this.pendingCount = images.length;
        return images;
    }

    /**
     * damaged = no se podrá enviar nunca (vacía o de un tipo que no se sube). Sin image ni damaged = no se
     * ha podido leer: puede ser un error puntual del navegador o que iOS haya perdido el fichero.
     */
    private async checkImage(db: IDBPDatabase<Db>, key: number): Promise<{ image?: Image, damaged: boolean }> {
        let image: Image | undefined;
        try {
            image = await db.get('images', key);
        } catch (error) {
            return {damaged: false};
        }

        if (!image) return {damaged: false};

        // Un POD que no es una foto (p. ej. el index.html de la app) no subirá nunca
        if (image.tipoFoto === "finalitzacioAlbara") {
            return this.isImageBase64(image.base64) ? {image, damaged: false} : {damaged: true};
        }

        if (image.tipoFoto !== "albaranFoto") return {damaged: true};

        if (image.bytes instanceof ArrayBuffer) {
            return image.bytes.byteLength > 0 ? {image, damaged: false} : {damaged: true};
        }

        // Encolada como File por una versión anterior: se pasa a bytes antes de que un envío la deje ilegible
        if (!(image.image instanceof Blob) || image.image.size === 0) return {damaged: true};

        try {
            await this.toBytes(image);
        } catch (error) {
            return {damaged: false};
        }

        await this.saveConverted(db, image);
        return {image, damaged: false};
    }

    private async toBytes(item: Image) {
        const file = item.image as File;
        const bytes = await file.arrayBuffer();
        if (bytes.byteLength === 0) throw new Error('La foto está vacía');

        item.bytes = bytes;
        item.fileName = file.name || 'foto.jpg';
        item.fileType = file.type || 'image/jpeg';
        delete item.image;
    }

    // Relee y guarda en la misma transacción: no recrea una foto que el xofer haya borrado mientras tanto
    private async saveConverted(db: IDBPDatabase<Db>, image: Image) {
        const tx = db.transaction('images', 'readwrite');
        const current = await tx.store.get(image.id as number);
        if (current && !(current.bytes instanceof ArrayBuffer)) {
            current.bytes = image.bytes;
            current.fileName = image.fileName;
            current.fileType = image.fileType;
            current.uploadId = current.uploadId ?? this.newUploadId();
            delete current.image;
            await tx.store.put(current);
        }
        image.uploadId = current?.uploadId ?? image.uploadId;
        await tx.done;
    }

    /**
     * La foto lista para enviar o previsualizar: un File nuevo en memoria cada vez, porque el que
     * iOS ha guardado en IndexedDB llega vacío al servidor.
     */
    async readFile(image: Image): Promise<File> {
        const db = await this.dbPromise;
        const stored = image.id === undefined ? undefined : await db.get('images', image.id);
        if (!(stored?.bytes instanceof ArrayBuffer) || stored.bytes.byteLength === 0) {
            throw new Error('No se ha podido leer la foto guardada');
        }

        return new File([stored.bytes], stored.fileName ?? 'foto.jpg', {type: stored.fileType ?? 'image/jpeg'});
    }

    private isImageBase64(base64: string): boolean {
        return !!base64 && DbService.IMAGE_BASE64_PREFIXES.some(prefix => base64.startsWith(prefix));
    }

    // Una foto ilegible se borra solo si sigue fallando varias veces durante un rato; si se lee bien, se recupera
    private isReadFailurePermanent(failures: Record<number, ReadFailure>, key: number): boolean {
        const now = Date.now();
        const failure = failures[key] ?? {count: 0, since: now};
        failure.count++;
        failures[key] = failure;

        return failure.count >= DbService.READ_FAILURES_TO_DELETE && now - failure.since >= DbService.READ_FAILURE_MIN_AGE_MS;
    }

    // En localStorage y no en el registro: un registro que no se puede leer tampoco se puede actualizar
    private loadReadFailures(): Record<number, ReadFailure> {
        try {
            return JSON.parse(localStorage.getItem(DbService.READ_FAILURES_KEY) ?? '{}');
        } catch (error) {
            return {};
        }
    }

    private saveReadFailures(failures: Record<number, ReadFailure>) {
        try {
            localStorage.setItem(DbService.READ_FAILURES_KEY, JSON.stringify(failures));
        } catch (error) {
            // Sin localStorage las marcas no se guardan: la foto ilegible no se borra, pero tampoco se cuenta
        }
    }

    /**
     * Sube las fotos que quedaron en cola (sin cobertura, o porque la subida falló) y borra de la
     * cola SOLO las que se han subido: una foto que no sube tiene que seguir esperando su turno.
     *
     * Van de una en una para no lanzar todas las subidas a la vez desde una conexión mala, y el
     * flag uploadingImages evita que el temporizador arranque una segunda tanda en paralelo, que
     * subiría dos veces la misma foto.
     */
    async uploadPhotos() {
        if (this.uploadingImages) return;

        this.uploadingImages = true;

        try {
            const images = await this.existsImages();

            for (const image of images) {
                if (!this.isSendable(image) || this.isWaitingRetry(image)) continue;

                try {
                    await this.sendImage(image);
                } catch (error) {
                    // La foto se queda en cola para el próximo intento. Sin conexión las siguientes fallarán
                    // igual y se deja para luego; si el servidor la rechaza, no debe bloquear las de detrás.
                    if (this.isConnectionError(error)) break;
                }
            }
        } finally {
            this.uploadingImages = false;
        }
    }

    /**
     * Reenvío manual desde la pantalla de fotos pendientes: sube solo esta foto, sin esperar a la tanda
     * ni a las que tiene delante. No arranca si la tanda está subiendo, para no enviar dos veces la misma.
     */
    async resendImage(image: Image) {
        if (this.uploadingImages) {
            throw new Error('Ya se están enviando fotos. Espera unos segundos y vuelve a probar.');
        }

        this.uploadingImages = true;

        try {
            await this.sendImage(image);
        } finally {
            this.uploadingImages = false;
        }
    }

    isSendable(image: Image): boolean {
        return image.tipoFoto === "albaranFoto" || image.tipoFoto === "finalitzacioAlbara";
    }

    private isWaitingRetry(image: Image): boolean {
        return !!image.retryAfter && Date.parse(image.retryAfter) > Date.now();
    }

    private async sendImage(image: Image) {
        try {
            if (image.tipoFoto === "albaranFoto") {
                const file = await this.readFile(image);
                await lastValueFrom(this.uploadService.uploadFotoLlegadasSalidas(file, image.albara, image.tipoFoto, image.idCheck, image.uploadId));
            } else if (image.tipoFoto === "finalitzacioAlbara") {
                await lastValueFrom(this.uploadService.uploadFotoAlbaran(image.base64, image.albara, image.tipoFoto, image.uploadId));
            } else {
                throw new Error('Tipo de foto desconocido: ' + image.tipoFoto);
            }
        } catch (error) {
            await this.recordFailure(image, error);
            throw error;
        }

        await this.deleteImage(image);
    }

    // Lee y escribe en la misma transacción: si el xofer ha borrado la foto mientras subía, no se recrea
    private async recordFailure(image: Image, error: unknown) {
        if (image.id === undefined) return;

        const db = await this.dbPromise;
        const tx = db.transaction('images', 'readwrite');
        const current = await tx.store.get(image.id);
        if (current) {
            current.attempts = (current.attempts ?? 0) + 1;
            current.lastError = this.describeError(error);
            current.lastAttemptOn = new Date().toISOString();
            // Sin conexión se reintenta en cuanto vuelva; si el servidor la rechaza, cada vez más espaciado
            if (this.isConnectionError(error)) {
                delete current.retryAfter;
            } else {
                const delays = DbService.RETRY_DELAYS_MINUTES;
                const minutes = delays[Math.min(current.attempts, delays.length) - 1];
                current.retryAfter = new Date(Date.now() + minutes * 60 * 1000).toISOString();
            }
            await tx.store.put(current);
        }
        await tx.done;
    }

    // status 0 = la petición no llegó al servidor (sin cobertura, timeout, CORS)
    private isConnectionError(error: unknown): boolean {
        return !(error instanceof HttpErrorResponse) || error.status === 0;
    }

    describeError(error: unknown): string {
        if (error instanceof HttpErrorResponse) {
            if (error.status === 0) return 'Sin conexión con el servidor';

            const message = error.error?.message;
            return 'Error ' + error.status + (typeof message === 'string' && message ? ': ' + message : '');
        }

        return error instanceof Error ? error.message : String(error);
    }

    async deleteImage(image: Image) {
        if (image.id === undefined) return;

        const db = await this.dbPromise;
        await db.delete('images', image.id);
        this.refreshPendingCount();
    }

    /**
     * Cuenta solo las fotos correctas, en segundo plano: releer la cola no debe retrasar ni hacer fallar
     * el guardado o el borrado de una foto. Las peticiones que llegan mientras cuenta se juntan en una.
     */
    refreshPendingCount() {
        if (this.refreshingCount) {
            this.refreshCountAgain = true;
            return;
        }

        this.refreshingCount = true;
        this.existsImages()
            .catch(error => console.error('Error al contar las fotos pendientes:', error))
            .finally(() => {
                this.refreshingCount = false;
                if (this.refreshCountAgain) {
                    this.refreshCountAgain = false;
                    this.refreshPendingCount();
                }
            });
    }

}
